import React from 'react';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  IconBus,
  IconBusOff,
  IconInfoCircle,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconScubaMask,
  IconScubaMaskOff,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import GenderOptionLabel from '../../../components/Form/GenderOptionLabel';
import { formatPrice } from '../../../common/utilities/priceFormatter';
import {
  getPhoneFlagElement,
  unknownPhoneFlagElement,
} from '../../../common/utilities/phoneFlag';
import {
  CERTIFICATE_LEVELS,
  CERTIFYING_AGENCIES,
  normalizeCertificateLevel,
} from '../../../constants/diveCertification';
import { GENDER_OPTIONS } from '../../../constants/gender';
import type { GuestFormValues, GuestFormErrors } from './GuestValidation';

const formatThb = (price: number) =>
  formatPrice(price, { currencySymbol: '฿' });

interface GuestFormFieldsProps {
  stage: number;
  formValues: GuestFormValues;
  errors: GuestFormErrors;
  needsDiveGear: boolean;
  checkedItems: { [key: string]: boolean };
  totalAmount: number;
  gearPrices: {
    bcd: number;
    wetsuit: number;
    fins: number;
    mask: number;
    regulator: number;
    diveComputer: number;
    torch: number;
  };
  onInputChange: (
    field: string,
  ) => (
    event:
      | React.ChangeEvent<HTMLInputElement | { value: unknown }>
      | SelectChangeEvent<string>,
  ) => void;
  onPhoneChange: (field: string, value: string) => void;
  onDateChange: (field: string, value: string) => void;
  onCheckboxChange: (
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onGearSelection: (event: React.ChangeEvent<HTMLInputElement>) => void;
  renderSizeChips: (
    item: 'bcd' | 'wetsuit' | 'fins',
    sizes: string[],
  ) => React.ReactNode;
}

const GuestFormFields: React.FC<GuestFormFieldsProps> = ({
  stage,
  formValues,
  errors,
  needsDiveGear,
  checkedItems,
  totalAmount,
  gearPrices,
  onInputChange,
  onPhoneChange,
  onDateChange,
  onCheckboxChange,
  onGearSelection,
  renderSizeChips,
}) => {
  switch (stage) {
    case 1:
      return (
        <>
          <div className="mt-8">
            <span className="text-xl font-semibold text-neutral-800">
              Personal details
            </span>
            <div className="mt-5 flex flex-col gap-5">
              <TextField
                fullWidth
                id="guest-full-name"
                label="Full name"
                variant="outlined"
                value={formValues.fullName}
                onChange={onInputChange('fullName')}
                error={!!errors.fullName}
                helperText={errors.fullName}
              />
              <FormControl fullWidth error={!!errors.gender}>
                <InputLabel id="guest-gender-label">Gender</InputLabel>
                <Select
                  labelId="guest-gender-label"
                  id="guest-gender"
                  label="Gender"
                  value={formValues.gender}
                  onChange={onInputChange('gender')}
                  displayEmpty
                  renderValue={selected =>
                    selected ? (
                      <GenderOptionLabel gender={selected} />
                    ) : (
                      <em>Select gender</em>
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Select gender</em>
                  </MenuItem>
                  {GENDER_OPTIONS.map(gender => (
                    <MenuItem key={gender} value={gender}>
                      <GenderOptionLabel gender={gender} />
                    </MenuItem>
                  ))}
                </Select>
                {!!errors.gender && (
                  <FormHelperText error>{errors.gender}</FormHelperText>
                )}
              </FormControl>
              <TextField
                fullWidth
                id="guest-email"
                label="Email address"
                variant="outlined"
                value={formValues.email}
                onChange={onInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
              />
              <MuiTelInput
                fullWidth
                id="guest-phone"
                label="Phone number"
                defaultCountry="TH"
                preferredCountries={['TH']}
                value={formValues.phone}
                onChange={value => onPhoneChange('phone', value)}
                error={!!errors.phone}
                helperText={errors.phone}
                getFlagElement={getPhoneFlagElement}
                unknownFlagElement={unknownPhoneFlagElement}
              />
            </div>
          </div>
        </>
      );

    case 2:
      return (
        <>
          <div className="mt-8">
            <span className="text-xl font-semibold text-neutral-800">
              Passport
            </span>
            <div className="mt-5 flex flex-col gap-5">
              <TextField
                fullWidth
                id="country-of-issuance"
                label="Country of issuance"
                variant="outlined"
                value={formValues.countryOfIssuance}
                onChange={onInputChange('countryOfIssuance')}
                error={!!errors.countryOfIssuance}
                helperText={errors.countryOfIssuance}
              />
              <TextField
                fullWidth
                id="passport-number"
                label="Passport number"
                variant="outlined"
                value={formValues.passportNumber}
                onChange={onInputChange('passportNumber')}
                error={!!errors.passportNumber}
                helperText={errors.passportNumber}
              />
              <TextField
                fullWidth
                id="passport-expiry-date"
                label="Passport expiry date"
                variant="outlined"
                value={formValues.expiryDate}
                onChange={onInputChange('expiryDate')}
                error={!!errors.expiryDate}
                helperText={errors.expiryDate}
              />
            </div>
          </div>
          <div className="mt-8">
            <span className="text-xl font-semibold text-neutral-800">
              Emergency contacts
            </span>
            <div className="mt-5 flex flex-col gap-5">
              <TextField
                fullWidth
                id="emergency-full-name"
                label="Full name"
                variant="outlined"
                value={formValues.emergencyContactName}
                onChange={onInputChange('emergencyContactName')}
                error={!!errors.emergencyContactName}
                helperText={errors.emergencyContactName}
              />
              <MuiTelInput
                fullWidth
                id="emergency-phone"
                label="Phone number"
                defaultCountry="TH"
                preferredCountries={['TH']}
                value={formValues.emergencyContactPhone}
                onChange={value =>
                  onPhoneChange('emergencyContactPhone', value)
                }
                error={!!errors.emergencyContactPhone}
                helperText={errors.emergencyContactPhone}
                getFlagElement={getPhoneFlagElement}
                unknownFlagElement={unknownPhoneFlagElement}
              />
            </div>
          </div>
        </>
      );

    case 3:
      return (
        <>
          <div className="mt-8">
            <span className="text-xl font-semibold text-neutral-800">
              Dive Certification
            </span>
            <div className="mt-5 flex flex-col gap-5">
              <FormControl fullWidth error={!!errors.certificateLevel}>
                <InputLabel id="guest-certificate-level-label">
                  Dive certification level
                </InputLabel>
                <Select
                  labelId="guest-certificate-level-label"
                  id="guest-certificate-level"
                  label="Dive certification level"
                  value={normalizeCertificateLevel(formValues.certificateLevel)}
                  onChange={onInputChange('certificateLevel')}
                >
                  {CERTIFICATE_LEVELS.map(level => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
                {!!errors.certificateLevel && (
                  <FormHelperText error>
                    {errors.certificateLevel}
                  </FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth error={!!errors.certifyingAgency}>
                <InputLabel id="guest-certifying-agency-label">
                  Certifying agency
                </InputLabel>
                <Select
                  labelId="guest-certifying-agency-label"
                  id="guest-certifying-agency"
                  label="Certifying agency"
                  value={formValues.certifyingAgency}
                  onChange={onInputChange('certifyingAgency')}
                >
                  {formValues.certifyingAgency &&
                    !(CERTIFYING_AGENCIES as readonly string[]).includes(
                      formValues.certifyingAgency,
                    ) && (
                      <MenuItem value={formValues.certifyingAgency}>
                        {formValues.certifyingAgency}
                      </MenuItem>
                    )}
                  {CERTIFYING_AGENCIES.map(agency => (
                    <MenuItem key={agency} value={agency}>
                      {agency}
                    </MenuItem>
                  ))}
                </Select>
                {!!errors.certifyingAgency && (
                  <FormHelperText error>
                    {errors.certifyingAgency}
                  </FormHelperText>
                )}
              </FormControl>
              <TextField
                fullWidth
                id="guest-certificate-number"
                label="Certification number"
                variant="outlined"
                value={formValues.certificateNumber}
                onChange={onInputChange('certificateNumber')}
                error={!!errors.certificateNumber}
                helperText={errors.certificateNumber}
              />
            </div>
          </div>
        </>
      );

    case 4:
      return (
        <>
          <div className="mt-8">
            <span className="text-xl font-semibold text-neutral-800">
              Airport transfer
            </span>
            <div className="mt-5">
              <FormControl>
                <RadioGroup
                  value={formValues.airportTransfer ? '1' : '0'}
                  name="airport-transfer-group"
                  onChange={event => {
                    const syntheticEvent = {
                      target: { value: event.target.value === '1' },
                    } as unknown as React.ChangeEvent<HTMLInputElement>;
                    onInputChange('airportTransfer')(syntheticEvent);
                  }}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={
                      <div className="flex items-center">
                        <IconBus size={20} />
                        <span className="ml-3 text-neutral-800">
                          Airport transfer needed
                        </span>
                        <span className="ml-4 text-green-300 font-semibold">
                          Free
                        </span>
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label={
                      <div className="flex items-center">
                        <IconBusOff size={20} />
                        <span className="ml-3 text-neutral-800">
                          No airport transfer needed
                        </span>
                      </div>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </div>
            <div className="mt-8">
              <span className="text-xl font-semibold text-neutral-800">
                Flight details
              </span>
            </div>
            <div className="mt-4 flex items-start gap-3 text-neutral-500">
              <IconInfoCircle size={20} className="mt-0.5 shrink-0" />
              <span className="text-sm">
                Airport transfer services are available only for embarkation and
                disembarkation
              </span>
            </div>
            <div className="mt-6 flex items-center">
              <IconPlaneArrival size={20} />
              <span className="ml-3 font-semibold text-neutral-800">
                Arrival flight
              </span>
            </div>
            <div className="mt-4 flex flex-col gap-5">
              <TextField
                fullWidth
                id="arrival-airline"
                label="Arrival flight number"
                variant="outlined"
                value={formValues.arrivalFlight}
                onChange={onInputChange('arrivalFlight')}
                error={!!errors.arrivalFlight}
                helperText={errors.arrivalFlight}
              />
              <DatePicker
                label="Arrival date"
                value={
                  formValues.arrivalDate ? dayjs(formValues.arrivalDate) : null
                }
                sx={{ width: '100%' }}
                onChange={newDate =>
                  onDateChange(
                    'arrivalDate',
                    newDate ? newDate.toISOString().split('T')[0] : '',
                  )
                }
              />
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.selfTransferUponArrival}
                      onChange={e => {
                        const syntheticEvent = {
                          target: { value: e.target.checked },
                        } as unknown as React.ChangeEvent<HTMLInputElement>;
                        onInputChange('selfTransferUponArrival')(
                          syntheticEvent,
                        );
                      }}
                    />
                  }
                  label="Self-transfer upon arrival"
                />
              </FormGroup>
            </div>
            <div className="mt-6 flex items-center">
              <IconPlaneDeparture size={20} />
              <span className="ml-3 font-semibold text-neutral-800">
                Departure flight
              </span>
            </div>
            <div className="mt-4 flex flex-col gap-5">
              <TextField
                fullWidth
                id="departure-airline"
                label="Departure flight number"
                variant="outlined"
                value={formValues.departureFlight}
                onChange={onInputChange('departureFlight')}
                error={!!errors.departureFlight}
                helperText={errors.departureFlight}
              />
              <DatePicker
                label="Departure date"
                value={
                  formValues.departureDate
                    ? dayjs(formValues.departureDate)
                    : null
                }
                sx={{ width: '100%' }}
                onChange={newDate =>
                  onDateChange(
                    'departureDate',
                    newDate ? newDate.toISOString().split('T')[0] : '',
                  )
                }
              />
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.selfTransferUponDeparture}
                      onChange={e => {
                        const syntheticEvent = {
                          target: { value: e.target.checked },
                        } as unknown as React.ChangeEvent<HTMLInputElement>;
                        onInputChange('selfTransferUponDeparture')(
                          syntheticEvent,
                        );
                      }}
                    />
                  }
                  label="Self-transfer upon departure"
                />
              </FormGroup>
            </div>
          </div>
        </>
      );

    case 5:
      return (
        <>
          <div className="mt-8">
            <span className="text-xl font-semibold text-neutral-800">
              Rental dive equipment
            </span>
            <div className="mt-5">
              <FormControl>
                <RadioGroup
                  value={needsDiveGear ? '1' : '0'}
                  onChange={onGearSelection}
                  name="dive-gear-group"
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={
                      <div className="flex items-center">
                        <IconScubaMask size={20} />
                        <span className="ml-3 text-neutral-800">
                          I need to rent dive gear
                        </span>
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label={
                      <div className="flex items-center">
                        <IconScubaMaskOff size={20} />
                        <span className="ml-3 text-neutral-800">
                          I don't need to rent dive gear
                        </span>
                      </div>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </div>
            {needsDiveGear && (
              <>
                <div className="mt-6">
                  <span className="text-xl text-neutral-800 font-semibold">
                    Choose your rental dive equipment
                  </span>
                  <div className="flex items-center mt-4 text-neutral-500">
                    <IconInfoCircle size={36} />
                    <span className="ml-3 text-sm">
                      If your dive equipment rental exceeds ฿3,000, a full
                      rental fee of ฿3,000 will apply
                    </span>
                  </div>
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 w-full flex items-center justify-between">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedItems.mask}
                        onChange={onCheckboxChange('mask')}
                      />
                    }
                    label={
                      <div className="flex justify-between w-full">
                        <span>Mask</span>
                      </div>
                    }
                  />
                  <span>{formatThb(gearPrices.mask)}</span>
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 w-full flex flex-col">
                  <div className="flex justify-between items-center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkedItems.bcd}
                          onChange={onCheckboxChange('bcd')}
                        />
                      }
                      label={
                        <div className="flex justify-between w-full">
                          <span>BCD</span>
                        </div>
                      }
                    />
                    <span>{formatThb(gearPrices.bcd)}</span>
                  </div>
                  {checkedItems.bcd &&
                    renderSizeChips('bcd', ['XS', 'S', 'M', 'L', 'XL', 'XXL'])}
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 w-full flex flex-col">
                  <div className="flex justify-between items-center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkedItems.wetsuit}
                          onChange={onCheckboxChange('wetsuit')}
                        />
                      }
                      label={
                        <div className="flex justify-between w-full">
                          <span>Wetsuit</span>
                        </div>
                      }
                    />
                    <span>{formatThb(gearPrices.wetsuit)}</span>
                  </div>
                  {checkedItems.wetsuit &&
                    renderSizeChips('wetsuit', [
                      'XS',
                      'S',
                      'M',
                      'L',
                      'XL',
                      'XXL',
                    ])}
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 w-full flex flex-col">
                  <div className="flex justify-between items-center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkedItems.fins}
                          onChange={onCheckboxChange('fins')}
                        />
                      }
                      label={
                        <div className="flex justify-between w-full">
                          <span>Fins</span>
                        </div>
                      }
                    />
                    <span>{formatThb(gearPrices.fins)}</span>
                  </div>
                  {checkedItems.fins &&
                    renderSizeChips('fins', [
                      '34-35',
                      '36-37',
                      '38-39',
                      '40-41',
                      '42-43',
                      '44-45',
                    ])}
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 w-full flex items-center justify-between">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedItems.regulator}
                        onChange={onCheckboxChange('regulator')}
                      />
                    }
                    label={
                      <div className="flex justify-between w-full">
                        <span>Regulator</span>
                      </div>
                    }
                  />
                  <span>{formatThb(gearPrices.regulator)}</span>
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 w-full flex items-center justify-between">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedItems.diveComputer}
                        onChange={onCheckboxChange('diveComputer')}
                      />
                    }
                    label={
                      <div className="flex justify-between w-full">
                        <span>Dive computer</span>
                      </div>
                    }
                  />
                  <span>{formatThb(gearPrices.diveComputer)}</span>
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 w-full flex items-center justify-between">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedItems.torch}
                        onChange={onCheckboxChange('torch')}
                      />
                    }
                    label={
                      <div className="flex justify-between w-full">
                        <span>Torch</span>
                      </div>
                    }
                  />
                  <span>{formatThb(gearPrices.torch)}</span>
                </div>
                <div className="mt-4">
                  <Divider />
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-neutral-800 font-semibold">
                      Total amount
                    </span>
                    <span className="text-sm text-neutral-500">
                      Pay on arrival date
                    </span>
                  </div>
                  <span className="text-neutral-800 font-semibold">
                    {formatThb(totalAmount)}
                  </span>
                </div>
                <div className="mt-4 flex items-center bg-brand-santtai-light py-4 px-3 rounded-lg text-brand-santtai-primary">
                  <div>
                    <IconInfoCircle size={20} />
                  </div>
                  <span className="ml-4 text-sm">
                    Note: Your rental fee will be collected on arrival date, no
                    payment needed now
                  </span>
                </div>
              </>
            )}
          </div>
        </>
      );

    default:
      return null;
  }
};

export default GuestFormFields;
